import { inject, injectable } from 'inversify';
import { center as store } from '@/store/modules';
import { IApiService, ICenterService } from '@/interfaces/Services';
import { CenterModel } from '@/models/center';
import DependencyIdentifiers from '@/ioc/DependencyIdentifiers';

type CenterResponse = {
  data: CenterModel
};

type PagedCenterResponse = {
  data: CenterModel[],
  meta: { total: number; }
};

@injectable()
export default class CenterService implements ICenterService {
  private _api: IApiService;

  constructor(@inject(DependencyIdentifiers.ApiService) api: IApiService) {
    this._api = api;
  }

  async getMany(page: number = 1, size: number = 20): Promise<[CenterModel[], number]> {
    const res = await this._api
      .get<PagedCenterResponse>(`/centers?size=${size}&page=${page}`, {
        skipCenterHeader: true
      });
    res.data.forEach((item: any) => store.cacheOne(item));
    return [res.data, res.meta.total];
  }

  async getById(id: string): Promise<CenterModel> {
    const center = await this._api
      .get<any>(`/centers/${id}`)
      .then(res => res.data);

    store.cacheOne(center);
    return center;
  }

  async create(data: Partial<CenterModel>): Promise<CenterModel> {
    const res = await this._api
      .post<CenterResponse>(`/centers`, { data }, { skipCenterHeader: true });
    store.cacheOne(res.data);
    return res.data;
  }

  async update(id: string, data: Partial<CenterModel>): Promise<CenterModel> {
    const res = await this._api
      .patch<CenterResponse>(`/centers/${id}`, { data }, { skipCenterHeader: true });
    store.cacheOne(res.data);
    return res.data;
  }
}
